import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  DirectionsWalk,
  DirectionsBike,
  DirectionsCar,
  DirectionsBus,
  DirectionsBoat,
  Hiking,
  ElectricBike,
  Kayaking,
  Accessible,
  Route as RouteIcon,
  Refresh,
  Clear,
  Delete,
} from '@mui/icons-material'
import { Edge } from '@xyflow/react'
import { useWorkflowStore, WorkflowNode } from '@store/workflowStore'
import { workflowDesignerApi } from '@api/endpoints/workflowDesigner'
import {
  RouteConfig,
  RouteTravelMode,
  RouteLineStyle,
  RouteArrowStyle,
  ROUTE_TRAVEL_MODES,
  ROUTE_LINE_STYLES,
  ROUTE_ARROW_STYLES,
} from '@features/adventures/types/workflow.types'

interface RoutePropertiesPanelProps {
  edge: Edge
  nodes: WorkflowNode[]
  adventureId: string
  onClose: () => void
}

// Icon mapping for travel modes
const travelModeIcons: Record<RouteTravelMode, React.ReactNode> = {
  'foot-walking': <DirectionsWalk />,
  'foot-hiking': <Hiking />,
  'cycling': <DirectionsBike />,
  'cycling-electric': <ElectricBike />,
  'driving': <DirectionsCar />,
  'public-transit': <DirectionsBus />,
  'ferry': <DirectionsBoat />,
  'water-kayak': <Kayaking />,
  'wheelchair': <Accessible />,
}

// Predefined colors for routes
const ROUTE_COLORS = [
  { value: '#9C27B0', label: 'Purple' },
  { value: '#2196F3', label: 'Blue' },
  { value: '#4CAF50', label: 'Green' },
  { value: '#FF9800', label: 'Orange' },
  { value: '#F44336', label: 'Red' },
  { value: '#00BCD4', label: 'Cyan' },
  { value: '#E91E63', label: 'Pink' },
  { value: '#795548', label: 'Brown' },
]

export function RoutePropertiesPanel({
  edge,
  nodes,
  adventureId,
  onClose,
}: RoutePropertiesPanelProps) {
  const updateRouteConfig = useWorkflowStore((state) => state.updateRouteConfig)
  const deleteEdge = useWorkflowStore((state) => state.deleteEdge)

  // Get route config from edge data
  const routeConfig = edge.data?.routeConfig as RouteConfig | undefined

  // Local state for form
  const [travelMode, setTravelMode] = useState<RouteTravelMode>(routeConfig?.travelMode ?? 'foot-walking')
  const [lineColor, setLineColor] = useState(routeConfig?.lineColor ?? '#9C27B0')
  const [lineStyle, setLineStyle] = useState<RouteLineStyle>(routeConfig?.lineStyle ?? 'dashed')
  const [lineOpacity, setLineOpacity] = useState(routeConfig?.lineOpacity ?? 0.5)
  const [arrowStyle, setArrowStyle] = useState<RouteArrowStyle>(routeConfig?.arrowStyle ?? 'none')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get source and target nodes
  const sourceNode = useMemo(() => nodes.find(n => n.id === edge.source), [nodes, edge.source])
  const targetNode = useMemo(() => nodes.find(n => n.id === edge.target), [nodes, edge.target])

  // Get location info
  const sourceLocation = sourceNode?.data.poiLocationConfig
  const targetLocation = targetNode?.data.poiLocationConfig

  // Check if both locations are valid
  const hasValidLocations = !!(
    sourceLocation?.latitude && sourceLocation?.longitude &&
    targetLocation?.latitude && targetLocation?.longitude
  )

  // Get cached route info
  const cachedRoute = routeConfig?.cachedRoute
  const isCacheValid = cachedRoute && new Date(cachedRoute.expiresAt) > new Date()

  // Format distance for display
  const formatDistance = (meters: number) => {
    if (meters < 1000) {
      return `${Math.round(meters)}m`
    }
    return `${(meters / 1000).toFixed(1)}km`
  }

  // Format duration for display
  const formatDuration = (seconds: number) => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`
    }
    if (seconds < 3600) {
      return `${Math.round(seconds / 60)}min`
    }
    const hours = Math.floor(seconds / 3600)
    const mins = Math.round((seconds % 3600) / 60)
    return `${hours}h ${mins}min`
  }

  // Handle save
  const handleSave = () => {
    updateRouteConfig(edge.id, {
      travelMode,
      lineColor,
      lineStyle,
      lineOpacity,
      arrowStyle,
    })
    onClose()
  }

  // Handle generate route
  const handleGenerateRoute = async () => {
    if (!hasValidLocations || !sourceLocation || !targetLocation) {
      setError('Both locations must have valid coordinates')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      // Use preview API - works for both saved and unsaved edges
      const result = await workflowDesignerApi.generateRoute(adventureId, {
        sourceLatitude: sourceLocation.latitude,
        sourceLongitude: sourceLocation.longitude,
        targetLatitude: targetLocation.latitude,
        targetLongitude: targetLocation.longitude,
        travelMode,
      })

      // Update the route config with the route data
      updateRouteConfig(edge.id, {
        travelMode,
        lineColor,
        lineStyle,
        lineOpacity,
        arrowStyle,
        cachedRoute: {
          coordinates: result.coordinates,
          distanceMeters: result.distanceMeters,
          durationSeconds: result.durationSeconds,
          cachedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate route')
    } finally {
      setIsGenerating(false)
    }
  }

  // Handle clear route (just clears local state - no API call needed for preview data)
  const handleClearRoute = () => {
    updateRouteConfig(edge.id, {
      travelMode,
      lineColor,
      lineStyle,
      lineOpacity,
      arrowStyle,
      cachedRoute: undefined,
    })
  }

  // Handle delete route edge
  const handleDeleteRoute = () => {
    deleteEdge(edge.id)
    onClose()
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RouteIcon color="primary" />
        Route Properties
      </DialogTitle>

      <DialogContent dividers>
        {/* Route endpoints info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Route Endpoints
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={sourceNode?.data.label || 'Unknown'}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography variant="body2" color="text.secondary">→</Typography>
            <Chip
              label={targetNode?.data.label || 'Unknown'}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </Box>
          {!hasValidLocations && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Both endpoints must have valid POI locations configured to generate a route.
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Travel mode */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Travel Mode</InputLabel>
          <Select
            value={travelMode}
            label="Travel Mode"
            onChange={(e) => setTravelMode(e.target.value as RouteTravelMode)}
          >
            {ROUTE_TRAVEL_MODES.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {travelModeIcons[mode.value]}
                  {mode.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Line color */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Line Color
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {ROUTE_COLORS.map((color) => (
              <Box
                key={color.value}
                onClick={() => setLineColor(color.value)}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: color.value,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: lineColor === color.value ? '3px solid #000' : '1px solid #ccc',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                  transition: 'transform 0.1s',
                }}
                title={color.label}
              />
            ))}
          </Box>
        </Box>

        {/* Line style */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Line Style</InputLabel>
          <Select
            value={lineStyle}
            label="Line Style"
            onChange={(e) => setLineStyle(e.target.value as RouteLineStyle)}
          >
            {ROUTE_LINE_STYLES.map((style) => (
              <MenuItem key={style.value} value={style.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 4,
                      backgroundColor: lineColor,
                      borderRadius: 2,
                      ...(style.value === 'dashed' && {
                        backgroundImage: `repeating-linear-gradient(90deg, ${lineColor} 0 8px, transparent 8px 16px)`,
                        backgroundColor: 'transparent',
                      }),
                      ...(style.value === 'dotted' && {
                        backgroundImage: `repeating-linear-gradient(90deg, ${lineColor} 0 4px, transparent 4px 8px)`,
                        backgroundColor: 'transparent',
                      }),
                    }}
                  />
                  {style.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Line opacity */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Line Opacity: {Math.round(lineOpacity * 100)}%
          </Typography>
          <Slider
            value={lineOpacity}
            onChange={(_, value) => setLineOpacity(value as number)}
            min={0.1}
            max={1}
            step={0.1}
            marks={[
              { value: 0.1, label: '10%' },
              { value: 0.5, label: '50%' },
              { value: 1, label: '100%' },
            ]}
          />
        </Box>

        {/* Arrow style */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Direction Arrows</InputLabel>
          <Select
            value={arrowStyle}
            label="Direction Arrows"
            onChange={(e) => setArrowStyle(e.target.value as RouteArrowStyle)}
          >
            {ROUTE_ARROW_STYLES.map((style) => (
              <MenuItem key={style.value} value={style.value}>
                {style.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Cached route info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Route Data
          </Typography>
          {cachedRoute && isCacheValid ? (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Distance: ${formatDistance(cachedRoute.distanceMeters)}`}
                size="small"
                color="success"
              />
              <Chip
                label={`Duration: ${formatDuration(cachedRoute.durationSeconds)}`}
                size="small"
                color="info"
              />
              <Chip
                label={`Expires: ${new Date(cachedRoute.expiresAt).toLocaleDateString()}`}
                size="small"
                variant="outlined"
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No route generated yet. Click "Generate Route" to fetch directions.
            </Typography>
          )}
        </Box>

        {/* Route action buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={isGenerating ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleGenerateRoute}
            disabled={!hasValidLocations || isGenerating}
            sx={{ flex: 1 }}
          >
            {isGenerating ? 'Generating...' : cachedRoute ? 'Regenerate Route' : 'Generate Route'}
          </Button>
          {cachedRoute && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Clear />}
              onClick={handleClearRoute}
              disabled={isGenerating}
            >
              Clear
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          onClick={handleDeleteRoute}
          color="error"
          startIcon={<Delete />}
        >
          Delete Route
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save Changes
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
